/**
 * Custom Header — behavior that cannot live in Liquid alone.
 * Search/CTA: Liquid. Cart count: ensure. Alle categorieën: outside-click + Escape.
 * Theme Editor: re-sync data-menu-style when switching mobile ↔ desktop.
 */

const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');

const ensureCartCount = () => {
  const count = document.querySelector('.header-actions__cart-icon .cart-bubble__text-count');
  if (!count) return;

  const ensure = () => {
    if (!count.textContent.trim()) {
      count.textContent = '0';
    }
  };

  ensure();
  new MutationObserver(ensure).observe(count, { childList: true, characterData: true, subtree: true });
};

/**
 * Close the root Alle categorieën <details> on outside click or Escape.
 * Accordion: one subcategory branch open at a time; Escape closes branch first.
 */
const initProductCategories = () => {
  const root = document.querySelector('details.custom-product-categories');
  if (!root) return;

  const rootSummary = root.querySelector(':scope > summary');

  const syncExpanded = () => {
    rootSummary?.setAttribute('aria-expanded', root.open ? 'true' : 'false');
  };

  const closeAllBranches = () => {
    root.querySelectorAll('.custom-product-categories__branch.is-open').forEach((branch) => {
      branch.classList.remove('is-open');
      const toggle = branch.querySelector('.custom-product-categories__link--toggle');
      toggle?.setAttribute('aria-expanded', 'false');
    });
  };

  const closeRoot = () => {
    cancelClose();
    closeAllBranches();
    if (root.open) root.open = false;
    syncExpanded();
  };

  let closeTimer = 0;

  const cancelClose = () => {
    window.clearTimeout(closeTimer);
    closeTimer = 0;
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer = window.setTimeout(() => {
      if (window.matchMedia('(min-width: 750px)').matches) {
        closeRoot();
      }
    }, 150);
  };

  syncExpanded();
  root.addEventListener('toggle', () => {
    if (!root.open) closeAllBranches();
    syncExpanded();
  });

  // Figma: open on hover (desktop), keep click for keyboard/touch
  root.addEventListener('pointerenter', () => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    cancelClose();
    root.open = true;
    syncExpanded();
  });
  root.addEventListener('pointerleave', () => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    scheduleClose();
  });

  root.addEventListener('click', (event) => {
    const toggle = event.target instanceof Element
      ? event.target.closest('.custom-product-categories__link--toggle')
      : null;
    if (!toggle || !root.contains(toggle)) return;

    event.preventDefault();
    event.stopPropagation();

    const branch = toggle.closest('.custom-product-categories__branch');
    if (!branch) return;

    const willOpen = !branch.classList.contains('is-open');

    root.querySelectorAll('.custom-product-categories__branch.is-open').forEach((openBranch) => {
      if (openBranch === branch) return;
      openBranch.classList.remove('is-open');
      openBranch
        .querySelector('.custom-product-categories__link--toggle')
        ?.setAttribute('aria-expanded', 'false');
    });

    branch.classList.toggle('is-open', willOpen);
    toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!root.open) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      closeRoot();
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !root.open) return;

    const openBranch = root.querySelector('.custom-product-categories__branch.is-open');
    if (openBranch) {
      openBranch.classList.remove('is-open');
      const toggle = openBranch.querySelector('.custom-product-categories__link--toggle');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.focus();
      event.preventDefault();
      return;
    }

    closeRoot();
    rootSummary?.focus();
    event.preventDefault();
  });
};

/**
 * Horizon switches to data-menu-style="drawer" when overflow-list hits
 * minimum-reached (or on touch). That sets `.header__column { display: contents }`
 * and demolishes the custom logo/search grid while resizing.
 *
 * ABC header: always "menu" from 750px up, always "drawer" below.
 * Keep this override here (not in Horizon utilities.js) so theme upgrades stay clean.
 * Horizontal links may still use Horizon "Mehr" overflow — that stays a single row.
 */
const syncHeaderMenuStyle = () => {
  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) return;

  const desired = DESKTOP_MQ.matches ? 'menu' : 'drawer';
  if (header.dataset.menuStyle !== desired) {
    header.dataset.menuStyle = desired;
  }
};

const initMenuStyleSync = () => {
  syncHeaderMenuStyle();

  DESKTOP_MQ.addEventListener('change', syncHeaderMenuStyle);
  window.addEventListener('resize', syncHeaderMenuStyle);
  document.addEventListener('shopify:section:load', syncHeaderMenuStyle);
  document.addEventListener('shopify:section:reorder', syncHeaderMenuStyle);
  document.addEventListener('shopify:block:select', syncHeaderMenuStyle);

  const header = document.querySelector('#header-component');
  if (header instanceof HTMLElement) {
    // Re-assert after Horizon utilities.js setHeaderMenuStyle (rAF / overflow / touch).
    new MutationObserver(() => {
      syncHeaderMenuStyle();
    }).observe(header, {
      attributes: true,
      attributeFilter: ['data-menu-style'],
    });
  }

  const overflowList = document.querySelector('#header-component overflow-list');
  if (overflowList) {
    new MutationObserver(syncHeaderMenuStyle).observe(overflowList, {
      attributes: true,
      attributeFilter: ['minimum-reached'],
    });
  }
};

const init = () => {
  ensureCartCount();
  initProductCategories();
  initMenuStyleSync();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
